// @ts-strict-ignore
import React, { ChangeEvent, useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  Combobox,
  DataListItem,
  Input,
  Radio,
  Sheet,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { DEFAULT_ZONE_OFFSET } from 'constants/timezones'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useCreatePaymentLimitMutation } from 'hooks/mutations/useCreatePaymentLimitMutation'
import { useUpdatePaymentLimitMutation } from 'hooks/mutations/useUpdatePaymentLimitMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { mapTimezonesToComboboxItems } from 'pages/PaymentLimits/mappers'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import type { BaseErrorResponse } from 'types/beneficiaries'
import {
  LimitLevel,
  LimitType,
  PaymentLimitSheetType,
  type AddLimitFormFields,
  type CreateLimitPayload,
  type EditLimitFormFields,
  type Limit,
  type UpdateLimitPayload,
} from 'types/paymentLimits'
import { addLimitSchema } from '../schemas/addLimitSchema'
import { editLimitSchema } from '../schemas/editLimitSchema'
import {
  CURRENCIES,
  formatPaymentMethodLabel,
  getAmountLabel,
  getLimitTypeOptions,
  getPaymentMethodOptionsForCurrency,
  normalizeZoneOffset,
} from '../utils'

type FormFields = AddLimitFormFields | EditLimitFormFields

export interface PaymentLimitSheetProps {
  isOpen: boolean
  type: PaymentLimitSheetType
  limit?: Limit | null
  customerId?: string
  level?: LimitLevel
  onClose: () => void
}

export const PaymentLimitSheet = ({
  isOpen,
  type,
  limit,
  customerId,
  level,
  onClose,
}: PaymentLimitSheetProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentAccount } = useAccountsContext()
  const { mutate: createLimit, isPending: isCreating } = useCreatePaymentLimitMutation()
  const { mutate: updateLimit, isPending: isUpdating } = useUpdatePaymentLimitMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const isEditMode = type === PaymentLimitSheetType.EDIT
  const isPending = isCreating || isUpdating

  const formSchema = isEditMode ? editLimitSchema(t) : addLimitSchema(t)

  const form = useForm<FormFields>({
    defaultValues: isEditMode
      ? {
          limitType: LimitType.TRANSACTION,
          amount: '',
        }
      : {
          currency: '',
          method: '',
          limitType: LimitType.TRANSACTION,
          amount: '',
          zoneOffset: DEFAULT_ZONE_OFFSET,
        },
    resolver: yupResolver(formSchema),
    mode: 'onChange',
  })

  const {
    register,
    setValue,
    trigger,
    reset,
    control,
    formState: { errors },
  } = form

  const formValues = useWatch({ control })
  const addFormValues = isEditMode ? null : (formValues as AddLimitFormFields)
  const addFormErrors = errors as Partial<Record<keyof AddLimitFormFields, { message?: string }>>

  useEffect(() => {
    if (!isOpen) return

    if (isEditMode && limit) {
      setValue('limitType', limit.limitType)
      setValue('amount', limit.amount > 0 ? String(limit.amount) : '')
    } else {
      reset({
        currency: '',
        method: '',
        limitType: LimitType.TRANSACTION,
        amount: '',
        zoneOffset: DEFAULT_ZONE_OFFSET,
      })
    }
    resetRequestError()
  }, [isOpen])

  const amountLabel = useMemo(() => {
    if (isEditMode) return t('paymentLimits.amountLabel')
    const limitType = formValues?.limitType || LimitType.TRANSACTION
    return getAmountLabel(limitType, t)
  }, [isEditMode, formValues, t])

  const methodLabel = limit
    ? formatPaymentMethodLabel(limit.method ?? limit.qualifiers?.method ?? '')
    : ''

  const currencyOptions = useMemo(
    () =>
      CURRENCIES.map(currency => ({
        value: currency,
        leftText: currency,
        LeftElement: <Avatar name={currency} type="currency" size="small" />,
      })),
    []
  )

  const paymentMethodOptions = useMemo(() => {
    if (!addFormValues?.currency) return []
    return getPaymentMethodOptionsForCurrency(addFormValues.currency)
  }, [addFormValues?.currency])

  const handleCurrencyChange = (value: string) => {
    setValue('currency', value, { shouldValidate: true })
    setValue('method', '')
  }

  const sheetTitle = isEditMode
    ? t('paymentLimits.editSheetTitle', { method: methodLabel })
    : t('paymentLimits.addSheetTitle')

  const amountInputTestId = isEditMode ? 'edit-limit-amount' : 'add-limit-amount'
  const submitButtonTestId = isEditMode ? 'change-limit-button' : 'submit-add-limit-button'
  const submitButtonLabel = isEditMode ? t('paymentLimits.changeLimit') : t('submit')
  const buttonClassName = isEditMode ? '' : 'pt-6'

  const timezoneOptions = useMemo(() => mapTimezonesToComboboxItems(), [])

  const handleSubmit = async () => {
    const isValid = await trigger()
    if (!isValid) return

    resetRequestError()

    if (!isEditMode && !currentAccount?.reference) {
      toast({
        header: t('paymentLimits.accountNotLoaded', {
          defaultValue:
            'Unable to create limit. Please wait for the account to load and try again.',
        }),
        variant: 'error',
      })
      return
    }

    if (isEditMode && limit) {
      const method = limit.method ?? limit.qualifiers?.method

      if (!limit.id || !method) {
        toast({
          header: t('paymentLimits.invalidLimitData'),
          variant: 'error',
        })
        return
      }

      const payload: UpdateLimitPayload = {
        id: limit.id,
        level: limit.level,
        method,
        limitType: formValues?.limitType as LimitType,
        amount: Number(formValues?.amount),
      }

      updateLimit(payload, {
        onSuccess: () => {
          toast({ header: t('paymentLimits.limitUpdatedSuccess'), variant: 'success' })
          queryClient.invalidateQueries({
            queryKey: [reactQueryName.PAYMENT_LIMITS],
          })
          onClose()
        },
        onError: (error: BaseErrorResponse) => {
          setRequestError({
            show: true,
            errorCode: error?.status,
            handledMessage: error?.data?.message || error?.message,
          })
          logSentryError('Error: useUpdatePaymentLimitMutation', error)
        },
      })
    } else {
      const addFormValues = formValues as AddLimitFormFields
      const payload: CreateLimitPayload = {
        level: customerId ? LimitLevel.CUSTOMER : (level ?? LimitLevel.ALL_CUSTOMERS),
        accountId: currentAccount?.reference,
        method: addFormValues?.method,
        limitType: addFormValues?.limitType as LimitType,
        amount: Number(addFormValues?.amount),
        currency: addFormValues?.currency,
        zoneOffset: normalizeZoneOffset(addFormValues?.zoneOffset),
        ...(customerId && { customerId }),
      }

      createLimit(payload, {
        onSuccess: () => {
          toast({ header: t('paymentLimits.limitCreatedSuccess'), variant: 'success' })
          queryClient.invalidateQueries({
            queryKey: [reactQueryName.PAYMENT_LIMITS],
          })
          onClose()
        },
        onError: (error: BaseErrorResponse) => {
          setRequestError({
            show: true,
            errorCode: error?.status,
            handledMessage: error?.data?.message || error?.message,
          })
          logSentryError('Error: useCreatePaymentLimitMutation', error)
        },
      })
    }
  }

  return (
    <Sheet isOpen={isOpen} header={sheetTitle} onClose={onClose}>
      <Box flex flexCol width="full" gap={24}>
        {isEditMode && limit ? (
          <Card variant="static" emphasis full>
            <Box flex justify="between">
              <DataListItem
                id="payment-method"
                layout="vertical"
                size="large"
                title={t('paymentLimits.paymentMethod')}
                body={methodLabel}
              />
              <DataListItem
                id="currency"
                layout="vertical"
                size="large"
                title={t('paymentLimits.currency')}
                BodyAvatar={<Avatar name={limit.currency} type="currency" size="small" />}
                body={limit.currency}
              />
            </Box>
          </Card>
        ) : (
          <>
            <Box>
              <Combobox
                key={`payment-limit-currency-${addFormValues?.currency}`}
                id="currency"
                testId="currency-combobox"
                label={t('paymentLimits.currency')}
                items={currencyOptions}
                selectPlaceholder={t('select', { defaultValue: 'Select' })}
                defaultValue={addFormValues?.currency}
                onChange={handleCurrencyChange}
                errorText={addFormErrors?.currency?.message}
              />
            </Box>

            <Box>
              <Combobox
                key={`payment-limit-method-${addFormValues?.method}`}
                id="payment-method"
                testId="payment-method-combobox"
                label={t('paymentLimits.paymentMethod')}
                items={paymentMethodOptions}
                selectPlaceholder={t('select')}
                defaultValue={addFormValues?.method}
                onChange={value => setValue('method', value, { shouldValidate: true })}
                disabled={!addFormValues?.currency}
                errorText={addFormErrors?.method?.message}
              />
            </Box>
          </>
        )}

        <Radio
          {...register('limitType')}
          label={t('paymentLimits.limitTypeLabel')}
          name="limitType"
          options={getLimitTypeOptions(t)}
          value={formValues?.limitType || LimitType.TRANSACTION}
          onChange={value => {
            setValue('limitType', value)
            if (value === LimitType.DAILY) {
              setValue('zoneOffset', DEFAULT_ZONE_OFFSET)
            } else {
              setValue('zoneOffset', undefined)
            }
          }}
          horizontal
          border
        />

        <Input
          {...register('amount')}
          label={amountLabel}
          type="text"
          inputMode="numeric"
          value={formValues.amount}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue('amount', e?.target?.value, { shouldDirty: true, shouldValidate: true })
          }
          testId={amountInputTestId}
          placeholderText="0.00"
          error={!!errors?.amount}
          errorText={errors?.amount?.message}
          required={!isEditMode}
        />

        {!isEditMode && formValues?.limitType === LimitType.DAILY && (
          <Combobox
            key={`payment-limit-timezone-${addFormValues?.zoneOffset}`}
            id="zoneOffset"
            testId="add-limit-timezone"
            label={t('paymentLimits.timezone')}
            items={timezoneOptions}
            selectPlaceholder={t('select', { defaultValue: 'Select' })}
            defaultValue={addFormValues?.zoneOffset ?? DEFAULT_ZONE_OFFSET}
            onChange={(value: string) => {
              setValue('zoneOffset', value, { shouldDirty: true, shouldValidate: true })
            }}
          />
        )}

        {requestError?.show && <RequestErrorCallout requestError={requestError} />}

        {isEditMode && (
          <>
            <Callout variant="info">{t('paymentLimits.editLimitInfoCallout')}</Callout>
            <Callout variant="warning">{t('paymentLimits.editLimitWarningCallout')}</Callout>
          </>
        )}

        <Box flex width="full" justify="between" className={buttonClassName}>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button testId={submitButtonTestId} loading={isPending} onClick={handleSubmit}>
            {submitButtonLabel}
          </Button>
        </Box>
      </Box>
    </Sheet>
  )
}
